import { AuthType } from '@thesavingsgroup/auth/AuthType';
import { DeliveryMechanism } from '@thesavingsgroup/auth/DeliveryMechanism';
import { HttpExceptionCode } from '@thesavingsgroup/http/errors/HttpExceptionCode';
import i18n from 'i18next';
import lodashGet from 'lodash/get';
import toString from 'lodash/toString';
import { StateCreator } from 'zustand';

import { DEFAULT_OTP_LENGTH } from '../../components/OTP/constants/otpLength';
import { DeliverPinLocation } from './enums/DeliverPinLocation';
import { extractPinDeliverErrorCode } from './functions/extractPinDeliverErrorCode';
import { extractPinDeliverErrorStatus } from './functions/extractPinDeliverErrorStatus';
import { DeliverPinParams } from './types/DeliverPinParams';
import { PinState } from './types/PinState';
import { PinStateDependencies } from './types/PinStateDependencies';

export const pinStore =
  ({
    useAnalyticStore,
    useApiStore,
  }: PinStateDependencies): StateCreator<PinState> =>
  (set, get) => ({
    deliverPinErrorCode: null,
    deliverPinFailedMessage: null,
    deliverPinRetrySuccess: false,
    deliverPinSuccess: false,
    pin: '',
    primaryPhoneNumber: null,

    computed: {
      get hasValidPin(): boolean {
        return get().pin.length === DEFAULT_OTP_LENGTH;
      },
      get isAccountOwnershipUnverified(): boolean {
        return (
          get().deliverPinErrorCode ===
          HttpExceptionCode.ACCOUNT_OWNERSHIP_UNVERIFIED
        );
      },
    },

    clearPin: (): void => {
      set({ pin: '' });
    },

    deliverPin: (params: DeliverPinParams): Promise<void> => {
      const {
        sendUserActivatingAccountAnalytics,
        sendUserActivatingApplicationAnalytics,
        sendUserAuthenticatingAnalytics,
        sendUserRegisteringAnalytics,
      } = useAnalyticStore.getState();

      set({
        deliverPinErrorCode: null,
        deliverPinFailedMessage: null,
        deliverPinRetrySuccess: false,
        deliverPinSuccess: false,
      });

      const { deliverPinLocation, email, phone, purpose } = params;

      if (purpose === AuthType.ACCOUNT_ACTIVATION_USING_PIN) {
        sendUserActivatingAccountAnalytics();
      }

      if (purpose === AuthType.APPLICATION_ACTIVATION_USING_PIN) {
        sendUserActivatingApplicationAnalytics();
      }

      if (purpose === AuthType.ACCOUNT_REGISTRATION_USING_PIN) {
        sendUserRegisteringAnalytics();
      }

      if (purpose === AuthType.ACCOUNT_SIGN_IN_USING_PIN) {
        sendUserAuthenticatingAnalytics();
      }

      const portalHttpRequest = useApiStore.getState().portalApi();

      return portalHttpRequest
        .post('pin/deliver', {
          deliverWith: DeliveryMechanism.SMS,
          email,
          phone,
          purpose,
        })
        .then((): void => {
          set({
            primaryPhoneNumber: phone,
            ...(deliverPinLocation === DeliverPinLocation.SIGN_IN && {
              deliverPinSuccess: true,
            }),
            ...(deliverPinLocation === DeliverPinLocation.RETRY && {
              deliverPinRetrySuccess: true,
            }),
          });
        })
        .catch((error: unknown): void => {
          // Extract the error code and HTTP status from the error response.
          // The error code (when present) gives us a more specific message than the HTTP status alone.
          const errorCode = extractPinDeliverErrorCode(error);
          const status = extractPinDeliverErrorStatus(error);

          // Resolve the HTTP status to a user-facing message, falling back to the default if the
          // status code isn't mapped.
          const statusMessage = lodashGet(
            i18n.t('errors.deliverPin.statusMessage'),
            toString(status),
            i18n.t('errors.deliverPin.defaultMessage'),
          );

          // Prefer the error code message when one is available, as it describes the delivery
          // failure more specifically (ex: landline, opted out, unreachable). Fall back to the
          // status message if the code isn't mapped or no code was returned.
          const deliverPinFailedMessage =
            errorCode === undefined
              ? statusMessage
              : lodashGet(
                  i18n.t('errors.deliverPin.errorCode'),
                  toString(errorCode),
                  statusMessage,
                );

          set({
            deliverPinErrorCode: errorCode ?? null,
            deliverPinFailedMessage,
          });
        });
    },

    setPin: (pin: string): void => {
      set({ pin });
    },
  });
